import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { actions as whatsappActions } from '../actions';
import { buildOccurrences, payloadOf, remainingPayloadOf, ruleFromMoments } from '../schedule';
import { colors } from '../theme';
import FieldLabel from './FieldLabel';
import ScheduleEditor from './ScheduleEditor';
import { kyivNow, toKyivWall } from '../kyivTime';

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  note: {
    color: colors.textDim,
    fontSize: '12px',
    lineHeight: 1.45,
    marginTop: theme.spacing(1.5),
  },
  section: { marginTop: theme.spacing(3) },
  error: { color: '#d32f2f', marginTop: theme.spacing(1.5) },
  saveButton: {
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': { backgroundColor: colors.accentHover },
  },
}));

// Время можно менять, пока рассылка ждёт и ничего ещё не ушло.
export const canEditTimes = (item) =>
  ['scheduled', 'paused', 'missed'].includes(item.status) && !item.startedAt && item.sent === 0;

// Правка рассылки: название и, пока ничего не отправлено, время. У серии правится
// расписание всех ещё не начавшихся рассылок сразу (даты и время каждой отправки).
function EditBroadcastDialog(props) {
  const classes = useStyles();
  const { item, items, onClose, onSaved } = props;

  const members = useMemo(
    () => (item.seriesId ? items.filter((entry) => entry.seriesId === item.seriesId) : [item]),
    [item, items]
  );
  const editableMembers = members.filter(canEditTimes);
  // У серии время правится, пока хоть одна её рассылка не началась.
  const timesEditable = editableMembers.length > 0;
  const hasPaused = editableMembers.some((entry) => ['paused', 'missed'].includes(entry.status));

  const [name, setName] = useState(item.name);
  const [rule, setRule] = useState(() =>
    ruleFromMoments(
      editableMembers.filter((entry) => entry.sendAt).map((entry) => toKyivWall(entry.sendAt)),
      kyivNow(),
      // Подпись серии хранит, как её создавали: по дням недели, по числам или по датам.
      item.seriesLabel || ''
    )
  );
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Новое время попало на ночь: сервер просит подтверждения, как в окне создания рассылки.
  const [quietAsked, setQuietAsked] = useState(false);
  const [quietOk, setQuietOk] = useState(false);

  const result = useMemo(() => buildOccurrences(rule, kyivNow()), [rule]);
  const nameChanged = name.trim() !== item.name;
  const changed = nameChanged || (timesEditable && scheduleTouched);

  const handleSave = async () => {
    setError('');
    if (!name.trim()) {
      setError('Введите название рассылки.');
      return;
    }
    if (timesEditable && scheduleTouched && result.problem) {
      setError(result.problem);
      return;
    }
    const changes = {};
    if (nameChanged) changes.name = name.trim();
    if (timesEditable && scheduleTouched) {
      const kept = members.filter((entry) => !canEditTimes(entry) && entry.sendAt);
      if (kept.length > 0) {
        Object.assign(changes, remainingPayloadOf(kept.map((entry) => entry.sendAt), result.list));
      } else {
        Object.assign(changes, payloadOf(rule, result.list));
      }
      if (quietOk) changes.allowQuietHours = true;
    }

    setBusy(true);
    const target = timesEditable && scheduleTouched ? editableMembers[0] : item;
    const answer = await props.updateBroadcast(target.id, changes);
    setBusy(false);
    if (!answer.ok) {
      // Ночь: показываем галочку, а не только красный текст, чтобы было как согласиться.
      if (answer.code === 'QUIET_HOURS') setQuietAsked(true);
      setError(answer.error);
      return;
    }
    onSaved();
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Изменить рассылку</DialogTitle>
      <DialogContent>
        <FieldLabel label="Название">
          <TextField
            fullWidth
            variant="outlined"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FieldLabel>

        {timesEditable ? (
          <div className={classes.section}>
            <Typography variant="subtitle2" gutterBottom>
              {item.seriesId ? 'Расписание серии' : 'Когда отправить'}
            </Typography>
            <ScheduleEditor
              rule={rule}
              result={result}
              onChange={(next) => {
                setRule(next);
                setScheduleTouched(true);
                // Другое время: прежнее согласие на ночь к нему не относится.
                setQuietAsked(false);
                setQuietOk(false);
                setError('');
              }}
            />
            {item.seriesId && members.length > editableMembers.length && (
              <Typography className={classes.note}>
                Уже отправленные или идущие рассылки серии ({members.length - editableMembers.length}) не
                меняются: новое расписание заменяет только ещё не начавшиеся.
              </Typography>
            )}
            {hasPaused && (
              <Typography className={classes.note}>
                Остановленные и пропущенные рассылки после сохранения снова встают в расписание.
              </Typography>
            )}
          </div>
        ) : (
          <Typography className={classes.note}>
            Время изменить нельзя: рассылка уже начала отправляться или завершилась. Можно поменять
            только название.
          </Typography>
        )}

        <Typography className={classes.note}>
          Шаблон, список и получателей менять нельзя: для этого создайте новую рассылку.
        </Typography>
        {error && <Typography className={classes.error}>{error}</Typography>}
        {quietAsked && (
          <FormControlLabel
            control={<Checkbox color="primary" checked={quietOk} onChange={(e) => setQuietOk(e.target.checked)} />}
            label="Всё равно запланировать на ночное время"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Отмена
        </Button>
        <Button
          className={classes.saveButton}
          variant="contained"
          disableElevation
          disabled={busy || !changed || (quietAsked && !quietOk)}
          onClick={handleSave}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default connect(null, mapDispatchToProps)(EditBroadcastDialog);
